/**
 * Company/bureau Tools pages for chatbot "how do I" answers.
 * Paths match tools-menu links (prefixed with /tools when building URLs).
 */
export type AdminPageLinkMode = 'bureau' | 'global' | 'globalCompanyPath';

export interface AdminNavigationPage {
  title: string;
  /** Relative tools path, e.g. /meeting_type/default_budget */
  link: string;
  keywords: string;
  /**
   * Mirrors tools-menu routing:
   * - global: /tools/...?company_id= (e.g. meeting type list)
   * - globalCompanyPath: /{company}.{bureau}/tools/... when company is selected
   * - bureau (default): /{company}.{bureau}/tools/...
   */
  linkMode?: AdminPageLinkMode;
}

export const ADMIN_NAVIGATION_PAGES: AdminNavigationPage[] = [
  // Event type & budgets
  {
    title: 'Edit Default Budgets',
    link: '/meeting_type/default_budget',
    keywords: 'default budget, default budgets, meeting type budget, budget defaults, update budget defaults',
  },
  {
    title: 'Event Profile & Timeline',
    link: '/meeting_type',
    linkMode: 'global',
    keywords: 'meeting type, event type, event profile, timeline, edit meeting type, update meeting type',
  },
  {
    title: 'Set Event Type Access',
    link: '/meeting_type/assign',
    keywords: 'event type access, assign meeting type',
  },
  {
    title: 'Program Fee Milestones',
    link: '/meeting_type/workflow_budget',
    keywords: 'program fee, workflow budget, milestones',
  },
  // Areas
  { title: 'Edit Areas', link: '/area', keywords: 'area, areas, territory' },
  { title: 'Edit Area Managers', link: '/area/manager', keywords: 'area manager' },
  { title: 'Import Sales Force', link: '/area/import', keywords: 'import sales force, sales force' },
  // Requests
  { title: 'Manage Requests', link: '/request', keywords: 'requests, program request' },
  { title: 'Rules for Event Requests', link: '/request/rule', keywords: 'request rules, event request rules' },
  { title: 'Create or Edit Request Form', link: '/request_form', keywords: 'request form' },
  // Rules
  { title: 'Business Rules Overview', link: '/rules/overview', keywords: 'business rules, rules overview' },
  { title: 'Holidays', link: '/rules/holidays', keywords: 'holidays' },
  { title: 'Attendance Requirements', link: '/rules/attendance_requirements', keywords: 'attendance requirements' },
  { title: 'Auto Cancel Rules', link: '/rules/auto_cancel', keywords: 'auto cancel' },
  { title: 'Closeout Rules', link: '/rules/closeout', keywords: 'closeout' },
  { title: 'Meal Type Default Rules', link: '/rules/meal_type_default', keywords: 'meal type default' },
  { title: 'Attendee Restrictions', link: '/rules/attendee_restriction', keywords: 'attendee restriction' },
  // User views
  {
    title: 'Edit User Types',
    link: '/client/level',
    linkMode: 'global',
    keywords: 'user types, client level',
  },
  { title: 'User View - Events', link: '/user_view/events', keywords: 'user view events' },
  { title: 'User View - Expenses', link: '/user_view/expenses', keywords: 'user view expenses' },
  // Dropdowns
  {
    title: 'Attendee Credentials',
    link: '/dropdown/attendee_degree/hcp',
    keywords: 'attendee credentials, attendee degree, hcp degree',
  },
  {
    title: 'Attendee Specialty',
    link: '/dropdown/attendee_specialty/hcp',
    keywords: 'attendee specialty',
  },
  { title: 'Audience Types', link: '/dropdown/attendee_type', keywords: 'audience type, attendee type dropdown' },
  { title: 'Contact Mode', link: '/dropdown/contact_mode', keywords: 'contact mode' },
  { title: 'Contact Response', link: '/dropdown/contact_response', keywords: 'contact response' },
  { title: 'Cancel Reasons', link: '/dropdown/cancel_reasons', keywords: 'cancel reason, cancellation' },
  { title: 'Checklist Late Reasons', link: '/dropdown/checklist_late_reason', keywords: 'checklist late' },
  { title: 'Client Level', link: '/dropdown/client_levels', keywords: 'client level dropdown' },
  { title: 'Program Format', link: '/dropdown/program_format', keywords: 'program format' },
  { title: 'Location Type', link: '/dropdown/location_type', keywords: 'location type' },
  { title: 'Meal Type', link: '/dropdown/meal_type', keywords: 'meal type dropdown' },
  { title: 'Series Type', link: '/dropdown/series_type', keywords: 'series type' },
  { title: 'Contract Types', link: '/dropdown/speaker_agreement_template', keywords: 'contract type, speaker agreement' },
  { title: 'Country', link: '/dropdown/country', keywords: 'country dropdown' },
  { title: 'Event Status', link: '/dropdown/EventStatus', keywords: 'event status' },
  { title: 'Fiscal Years', link: '/dropdown/fiscal_year', keywords: 'fiscal year dropdown' },
  { title: 'Forum Type', link: '/dropdown/forum_type', keywords: 'forum type' },
  { title: 'HCP Type', link: '/dropdown/hcp_type', keywords: 'hcp type' },
  { title: 'Hold Reasons', link: '/dropdown/hold_reason', keywords: 'hold reason' },
  { title: 'Decline Reasons', link: '/dropdown/decline_reason', keywords: 'decline reason' },
  { title: 'Payment Status', link: '/dropdown/payment_status', keywords: 'payment status' },
  { title: 'Profession', link: '/dropdown/profession', keywords: 'profession dropdown' },
  { title: 'Salutation', link: '/dropdown/salutation', keywords: 'salutation' },
  { title: 'Speaker Specialty', link: '/dropdown/speaker_specialty/bureau', keywords: 'speaker specialty' },
  { title: 'Speaker Type', link: '/dropdown/speaker_type', keywords: 'speaker type' },
  { title: 'State/Province', link: '/dropdown/state', keywords: 'state, province' },
  { title: 'Vendor Types', link: '/dropdown/vendor_type', keywords: 'vendor type' },
  { title: 'Suffix', link: '/dropdown/suffix', keywords: 'suffix dropdown' },
  // Vendors & speakers
  { title: 'Venues', link: '/vendor/venue', keywords: 'venue, venues' },
  {
    title: 'Topic Profiles',
    link: '/topic',
    linkMode: 'global',
    keywords: 'topic, topics, presentation topic',
  },
  {
    title: 'Task List Library',
    link: '/checklist',
    linkMode: 'globalCompanyPath',
    keywords: 'checklist, task list',
  },
  { title: 'Workflows', link: '/workflow', linkMode: 'global', keywords: 'workflow' },
  { title: 'Company Email Templates', link: '/email_template', keywords: 'email template' },
  { title: 'Notifications', link: '/notification', keywords: 'notification' },
];
